import type { ReactNode } from 'react';

import type { RemixHiddenOptionKind } from '@atlassian/malleable-ui-components/common/types';

import { useCanPerformContentOperation } from '@confluence/entity-operations/entry-points/entityOperationsHelpers.ts';
import { OperationTypes } from '@confluence/entity-operations/entry-points/operationsTypes.ts';
import { expValEquals, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { usePageContentId, usePageSpaceKey } from '@confluence/page-context';
import { useSpaceDetail } from '@confluence/space-utils/entry-points/useSpaceDetail.ts';

const NO_HIDDEN_OPTIONS: readonly RemixHiddenOptionKind[] = [];
const ATTACHMENT_PERMISSION_HIDDEN_OPTIONS = [
	'infographic',
	'image',
] as const satisfies readonly RemixHiddenOptionKind[];
const DIAGRAM_HIDDEN_OPTION = ['diagram'] as const satisfies readonly RemixHiddenOptionKind[];

/**
 * Prefetches options that cannot be completed in the current space and reports whether the result
 * should suppress the modal's own lookup. Diagrams finalize as whiteboards, while the other
 * space-dependent options map directly to their corresponding content types.
 */
export const useSpaceRemixOptionsPrefetch = ({
	enabled = true,
}: {
	enabled?: boolean;
} = {}): {
	didPrefetch: boolean;
	hiddenOptions: readonly RemixHiddenOptionKind[];
} => {
	const [spaceKey] = usePageSpaceKey();
	const { data: spaceDetail } = useSpaceDetail(spaceKey, {
		includeSpaceTypeSettings: true,
		skip: !enabled,
	});
	if (!enabled) {
		return { didPrefetch: false, hiddenOptions: NO_HIDDEN_OPTIONS };
	}

	const isWhiteboardsEnabled =
		spaceDetail?.typeSettings?.enabledContentTypes?.isWhiteboardsEnabled ?? false;
	const enabledContentTypes = spaceDetail?.typeSettings?.enabledContentTypes;
	const hiddenOptions: RemixHiddenOptionKind[] = [];

	// Only hide the diagram option when whiteboards are unavailable for the space.
	if (!isWhiteboardsEnabled) {
		hiddenOptions.push('diagram');
	}

	if (!enabledContentTypes?.isWhiteboardsEnabled) {
		hiddenOptions.push('whiteboard');
	}
	if (!enabledContentTypes?.isDatabasesEnabled) {
		hiddenOptions.push('database');
	}
	if (!enabledContentTypes?.isSlidesEnabled) {
		hiddenOptions.push('slides');
	}
	return {
		didPrefetch: true,
		hiddenOptions: hiddenOptions.length > 0 ? hiddenOptions : NO_HIDDEN_OPTIONS,
	};
};

export const useShouldHideDiagramRemixOption = (
	options: {
		enabled?: boolean;
	} = {},
): readonly RemixHiddenOptionKind[] => useSpaceRemixOptionsPrefetch(options).hiddenOptions;

/**
 * Resolves attachment-backed Remix eligibility independently from space-type settings so it can be
 * prefetched without moving the existing space-details query onto general page load. `didPrefetch`
 * is false only when the experiment requires a permission lookup but no content ID is available.
 */
export const useAttachmentRemixOptionsPrefetch = ({
	enabled = true,
	contentId: contentIdOverride,
}: {
	enabled?: boolean;
	contentId?: string;
} = {}): {
	didPrefetch: boolean;
	hiddenOptions: readonly RemixHiddenOptionKind[];
} => {
	const [pageContentId] = usePageContentId();
	const contentId = contentIdOverride ?? pageContentId;
	const isAttachmentPermissionsExperimentEnabled =
		enabled && expValEquals('cc_maui_remix_attachment_permissions', 'isEnabled', true);
	const permissionContentId =
		isAttachmentPermissionsExperimentEnabled && contentId ? contentId : '';
	const canCreateAttachment = useCanPerformContentOperation({
		contentId: permissionContentId,
		operation: OperationTypes.CREATE,
		contentType: 'attachment',
	});
	const hiddenOptions =
		permissionContentId && canCreateAttachment !== true
			? ATTACHMENT_PERMISSION_HIDDEN_OPTIONS
			: NO_HIDDEN_OPTIONS;

	// Fail closed while a real content permission is loading, but do not conflate missing page
	// context with a denial. Finalization still enforces permission server-side.
	return {
		didPrefetch:
			enabled && (!isAttachmentPermissionsExperimentEnabled || Boolean(permissionContentId)),
		hiddenOptions,
	};
};

/** Returns only the hidden options for consumers that do not need prefetch-status metadata. */
export const useShouldHideAttachmentRemixOptions = (
	options: {
		enabled?: boolean;
		contentId?: string;
	} = {},
): readonly RemixHiddenOptionKind[] => useAttachmentRemixOptionsPrefetch(options).hiddenOptions;

/**
 * Returns the common options Remix pickers should hide. Individual entry points can compose these
 * with additional option-specific restrictions, such as whiteboard, database, or slides support.
 */
export const useCommonRemixHiddenOptions = ({
	enabled = true,
	contentId,
}: {
	enabled?: boolean;
	contentId?: string;
} = {}): readonly RemixHiddenOptionKind[] => {
	const attachmentOptions = useShouldHideAttachmentRemixOptions({ enabled, contentId });
	const isAttachmentPermissionsExperimentEnabled =
		enabled &&
		// The attachment hook above records the exposure; this read keeps diagram behavior aligned.
		// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
		UNSAFE_expValEqualsNoExposure('cc_maui_remix_attachment_permissions', 'isEnabled', true);
	const unavailableSpaceOptions = useShouldHideDiagramRemixOption({
		enabled: isAttachmentPermissionsExperimentEnabled,
	});
	const diagramOptions = unavailableSpaceOptions.includes('diagram') ? DIAGRAM_HIDDEN_OPTION : [];

	return attachmentOptions.length > 0 || diagramOptions.length > 0
		? [...attachmentOptions, ...diagramOptions]
		: NO_HIDDEN_OPTIONS;
};

export const CommonRemixHiddenOptionsResolver = ({
	children,
	contentId,
	enabled,
}: {
	children: (hiddenOptions: readonly RemixHiddenOptionKind[]) => ReactNode;
	contentId?: string;
	enabled?: boolean;
}): ReactNode => children(useCommonRemixHiddenOptions({ contentId, enabled }));
